/**
 * The `/warehouse` module's own registry — OneSphere's warehouse console, built the way `/fleet` is (owner,
 * 2026-09-26): its sections, the screens under each, who may open them, and one source for the module's routes
 * (`pages/warehouse/index.tsx`), its sub-navigation, the navigation registry (`lib/nav.ts`) and the tests.
 *
 * The module is an **extension over ERPNext's stock**, as the Inventory entries in the navigation always were: the
 * warehouses, bins, stock entries and serial numbers are the ERP's records, and the screens here are the working
 * surfaces the ERP does not give — starting with the one place a warehouse has that a doctype list cannot represent.
 *
 * **The gate is not a warehouse-only screen** (owner, 2026-09-26). A security gate is a place where somebody is
 * answerable for what passes, and that is as true of a plant entrance, a store's back door or a site barrier as of a
 * distribution centre. It lives in this module because a warehouse is where most loads leave, and it takes what is
 * presented at the door as its subject rather than assuming a warehouse around it.
 *
 * The sections are listed in the order of a day's work — goods arrive (`inbound`, wave B), are picked for the
 * customers waiting for them (`picking`, wave C), are moved to where they sell from (`moves`, wave D), are counted
 * (`counts`, wave E), are looked up and watched for expiry (`enquiry`, wave F), the month is closed over them
 * (`period`, wave G), and they leave through the door (`gate`, wave A) — and the dock comes first because it is where
 * the identity of a good is decided.
 *
 * The realm gained **`os_storekeeper`** and **`os_security`** on 2026-09-26, so the people whose job this is can now
 * open it — and, as importantly, the ERPNext permissions behind those roles exist too. Two gates have to agree: a
 * OneSphere role opens the screen, and a Frappe permission lets the document be written. Until that day the second
 * gate had nobody behind it, and the storekeeper whose job is counting could not read a count.
 *
 * The **release** at the gate has a permission of its own since 2026-09-26 — OneFleet's `fleet-ops:orders:release`,
 * held by `of_security` — and it is deliberately not `orders:dispatch`: one is a person at a door saying these goods
 * may go, the other is deciding who drives and when. `os_security` maps to `of_security`, so a guard now opens the
 * screen *and* can let the lorry out, and can do nothing else with an order.
 */
import { OF_ROLE } from "../onefleet/roles";

export type WarehouseScreen = {
  key: string;
  /** Path under the section (no leading slash; "" is the section's own page). */
  path: string;
  /** i18n key of the label. */
  label: string;
  roles?: readonly string[];
};

export type WarehouseSection = {
  key: string;
  /** Path segment under `/warehouse`. */
  path: string;
  label: string;
  roles: readonly string[];
  screens: WarehouseScreen[];
};

export const WAREHOUSE_BASE = "/warehouse";

const OS_EXEC = ["os_executive", "os_admin"] as const;
/** Who answers for goods leaving a site: the guard first, and beside them the roles that were doing it before the
 * realm had a gate role of its own. */
const GUARDS = [
  "os_security",
  "os_storekeeper",
  "os_store_manager",
  "os_plant_gm",
  "os_operator",
  "os_maintenance",
  "os_procurement",
  ...OS_EXEC,
] as const;
/** Who works a receiving dock. The same list the API enforces (`api/warehouse.py::RECEIVE_ROLES`) — the storekeeper
 * first, and beside them the roles that already answered for goods arriving at a site. */
const DOCK = [
  "os_storekeeper",
  "os_store_manager",
  "os_procurement",
  "os_maintenance",
  "os_plant_gm",
  ...OS_EXEC,
] as const;
/** The release is a OneFleet permission (`fleet-ops:orders:release`), so the roles that hold it open the screen too —
 * the gate officer whose whole job it is, and the three who could always release a load. */
/** Who may look at stock: the same list the API enforces (`api/warehouse.py::ENQUIRY_ROLES`). The widest in the
 * module, because the screen writes nothing — and still not everybody. **The gate officer is not here**: a guard's
 * ERPNext grants stop at what a load claims to be carrying and do not include `Bin`, so the screen would open and the
 * first query would refuse. A screen that opens and then refuses is worse than one that does not open. */
const ENQUIRY = [
  "os_storekeeper",
  "os_store_manager",
  "os_procurement",
  "os_maintenance",
  "os_plant_gm",
  "os_operator",
  "os_finance",
  "os_sales",
  ...OS_EXEC,
] as const;
const FLEET = [
  OF_ROLE.security,
  OF_ROLE.dispatcher,
  OF_ROLE.admin,
  OF_ROLE.fleetManager,
] as const;

export const WAREHOUSE_MODULE_ROLES: readonly string[] = [
  ...GUARDS,
  "os_finance",
  ...FLEET,
];

export const WAREHOUSE_SECTIONS: WarehouseSection[] = [
  {
    key: "inbound",
    path: "inbound",
    label: "wh.sections.inbound",
    roles: [...DOCK],
    screens: [],
  },
  {
    key: "picking",
    path: "picking",
    label: "wh.sections.picking",
    roles: [...DOCK],
    screens: [],
  },
  {
    key: "moves",
    path: "moves",
    label: "wh.sections.moves",
    roles: [...DOCK],
    screens: [],
  },
  {
    key: "counts",
    path: "counts",
    label: "wh.sections.counts",
    roles: [...DOCK],
    screens: [],
  },
  {
    key: "enquiry",
    path: "enquiry",
    label: "wh.sections.enquiry",
    // the widest list in the module, and deliberately: looking at a shelf is not a privilege, and the screen writes
    // nothing — every action its exceptions call for belongs to the wave that owns it
    roles: [...ENQUIRY],
    screens: [],
  },
  {
    key: "period",
    path: "period",
    label: "wh.sections.period",
    // the same list as the enquiry: a storekeeper needs to know whether the month they are posting into is still
    // open, and being refused at save time is the worst way to find out. Asking for a close is narrower, and the
    // API decides that, not the route
    roles: [...ENQUIRY],
    screens: [],
  },
  {
    key: "gate",
    path: "gate",
    label: "wh.sections.gate",
    roles: [...GUARDS, ...FLEET],
    screens: [],
  },
];

export function warehousePath(
  section: WarehouseSection,
  screen?: WarehouseScreen,
): string {
  return [WAREHOUSE_BASE, section.path, screen?.path]
    .filter((p) => p && p.length > 0)
    .join("/");
}

/** The section a pathname under `/warehouse` belongs to. */
export function sectionForPath(pathname: string): WarehouseSection | undefined {
  if (!pathname.startsWith(WAREHOUSE_BASE)) return undefined;
  const seg =
    pathname.slice(WAREHOUSE_BASE.length).split("/").filter(Boolean)[0] ?? "";
  return WAREHOUSE_SECTIONS.find((x) => x.path === seg);
}
